import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { SetupCopyBlock } from "@/components/setup/SetupCopyBlock";
import { SetupExamplePrompts } from "@/components/setup/SetupExamplePrompts";
import {
	AGENTREACHER_MCP_URL,
	CLAUDE_MCP_ADD_COMMAND,
} from "@/components/setup/setup-config";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/connect-claude";
const CLAUDE_SECTION_ID = "claude";
const CLAUDE_CODE_SECTION_ID = "claude-code";

export const Route = createFileRoute(path)({
	component: ConnectClaude,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "Connect Claude to AgentReacher",
			description:
				"Three-step walkthrough to add the AgentReacher MCP server to Claude. Open Connectors, add the custom connector, paste the URL, and start drafting posts.",
		}),
});

type SetupStep = {
	title: string;
	body: string;
	image?: string;
	alt?: string;
	visual: ReactNode;
	wide?: boolean;
};

const STEPS = [
	{
		title: "Open Connectors Page",
		body: "Go to Claude Connectors and click Add custom connector.",
		image: "/claude-setup/1.connectors_page.webp",
		alt: "Claude Connectors page with the Add custom connector button",
		visual: (
			<div className="grid gap-3 p-4">
				<div className="h-4 w-28 rounded bg-muted-foreground/20" />
				<div className="grid gap-2 rounded-md border bg-background p-3">
					<div className="h-3 w-40 rounded bg-muted-foreground/20" />
					<div className="h-3 w-56 rounded bg-muted-foreground/10" />
				</div>
				<div className="h-9 w-44 rounded-md bg-primary" />
			</div>
		),
	},
	{
		title: "Add AgentReacher Connector",
		body: "Use AgentReacher.com as the connector name and paste the AgentReacher MCP URL. Leave Advanced settings empty.",
		image: "/claude-setup/2.add_connector.webp",
		alt: "Claude custom connector form filled with AgentReacher name and MCP URL",
		visual: (
			<div className="grid gap-3 p-4">
				<div className="h-4 w-36 rounded bg-muted-foreground/20" />
				<div className="grid gap-2">
					<div className="rounded-md border bg-background px-3 py-2 text-xs text-foreground">
						AgentReacher.com
					</div>
					<div className="rounded-md border bg-background px-3 py-2 text-xs text-foreground">
						{AGENTREACHER_MCP_URL}
					</div>
					<div className="rounded-md border border-dashed bg-background px-3 py-2 text-xs text-muted-foreground">
						Advanced settings empty
					</div>
				</div>
			</div>
		),
	},
	{
		title: "Start using the tools",
		body: "Once Claude confirms the connector, AgentReacher tools are available in claude.ai.",
		image: "/claude-setup/3.filled_in_connector.webp",
		alt: "Claude showing the connected AgentReacher tools",
		visual: (
			<div className="grid gap-3 p-4">
				<div className="h-4 w-32 rounded bg-muted-foreground/20" />
				{["postCompose", "postCompose", "integrationList"].map((tool) => (
					<div
						className="rounded-md border bg-background px-3 py-2 font-mono text-xs text-foreground"
						key={tool}
					>
						{tool}
					</div>
				))}
			</div>
		),
	},
] satisfies SetupStep[];

const CLAUDE_CODE_STEPS = [
	{
		title: "Run the MCP command",
		body: "Open your terminal and add AgentReacher to Claude Code with the hosted HTTP MCP command.",
		image: "/claude-code-setup/claude_code_1.webp",
		alt: "Terminal showing the Claude Code MCP add command",
		wide: true,
		visual: (
			<div className="p-4">
				<div className="rounded-md bg-zinc-950 p-4 font-mono text-xs leading-6 text-zinc-100">
					<span className="text-zinc-500">$ </span>
					{CLAUDE_MCP_ADD_COMMAND}
				</div>
			</div>
		),
	},
	{
		title: "Approve the connection",
		body: "Start Claude Code in your terminal, run /mcp, then re-authenticate AgentReacher. Claude Code opens a link you can click to approve the connection.",
		image: "/claude-code-setup/claude_code_2.webp",
		alt: "AgentReacher sign-in and authorization flow for Claude Code",
		wide: true,
		visual: (
			<div className="grid gap-3 p-4">
				<div className="h-4 w-40 rounded bg-muted-foreground/20" />
				<div className="rounded-md border bg-background p-4">
					<div className="h-3 w-52 rounded bg-muted-foreground/20" />
					<div className="mt-3 h-8 w-28 rounded-md bg-primary" />
				</div>
			</div>
		),
	},
	{
		title: "Use AgentReacher in Claude Code",
		body: "After setup, Claude Code can draft, schedule, and publish social posts through AgentReacher tools.",
		image: "/claude-code-setup/claude_code.webp",
		alt: "Claude Code showing available AgentReacher MCP tools",
		wide: true,
		visual: (
			<div className="grid gap-2 p-4 font-mono text-xs">
				<div className="rounded-md bg-zinc-950 px-3 py-2 text-zinc-100">
					agentreacher:workspaceContext
				</div>
				<div className="rounded-md bg-zinc-950 px-3 py-2 text-zinc-100">
					agentreacher:postCompose
				</div>
				<div className="rounded-md bg-zinc-950 px-3 py-2 text-zinc-100">
					agentreacher:postCompose
				</div>
			</div>
		),
	},
] satisfies SetupStep[];

function StepVisual({ step }: { step: SetupStep }) {
	if (step.image) {
		return (
			<img
				src={step.image}
				alt={step.alt}
				loading="lazy"
				decoding="async"
				className="block h-auto w-full object-cover"
			/>
		);
	}

	return <div className="min-h-44 bg-muted/30">{step.visual}</div>;
}

function SetupSection({
	id,
	title,
	description,
	steps,
	variant,
	switchLink,
}: {
	id: string;
	title: string;
	description: string;
	steps: SetupStep[];
	variant: "claude" | "claude-code";
	switchLink: { href: string; label: string };
}) {
	return (
		<section
			id={id}
			className="scroll-mt-24 rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-14"
		>
			<div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<h2 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">
						{title}
					</h2>
					<p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
						{description}
					</p>
				</div>
				<a
					href={switchLink.href}
					className="inline-flex w-fit items-center rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-foreground/20 hover:bg-muted/40"
				>
					{switchLink.label}
				</a>
			</div>
			<div className="grid gap-5">
				{steps.map((step, index) => (
					<div
						className={`grid gap-4 rounded-md border bg-background p-4 sm:p-5 ${
							step.wide ? "" : "sm:grid-cols-[0.95fr_1.05fr]"
						}`}
						key={step.title}
					>
						<div className="flex gap-3">
							<span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
								{index + 1}
							</span>
							<div className="min-w-0 flex-1">
								<h3 className="flex h-8 items-center text-base font-semibold text-foreground">
									{step.title}
								</h3>
								<p className="mt-1 text-sm leading-6 text-muted-foreground">
									{step.body}
								</p>
								{variant === "claude" && index === 0 ? (
									<a
										href="https://claude.ai/customize/connectors"
										target="_blank"
										rel="noreferrer"
										className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-foreground/20 hover:bg-muted/40"
									>
										Open Claude Connectors
										<ExternalLink className="size-3.5" />
									</a>
								) : null}
								{variant === "claude-code" && index === 0 ? (
									<div className="mt-3">
										<SetupCopyBlock text={CLAUDE_MCP_ADD_COMMAND} prefix="$" />
									</div>
								) : null}
								{variant === "claude" && index === 1 ? (
									<div className="mt-3 grid gap-2">
										<div className="grid gap-1.5">
											<p className="text-xs font-medium text-muted-foreground">
												Name
											</p>
											<SetupCopyBlock text="AgentReacher.com" />
										</div>
										<div className="grid gap-1.5">
											<p className="text-xs font-medium text-muted-foreground">
												Remote MCP server URL
											</p>
											<SetupCopyBlock text={AGENTREACHER_MCP_URL} />
										</div>
									</div>
								) : null}
							</div>
						</div>
						<div className="overflow-hidden rounded-md border bg-muted/30">
							<StepVisual step={step} />
						</div>
					</div>
				))}
			</div>
		</section>
	);
}

function ConnectClaude() {
	return (
		<MarketingPageShell>
			<MarketingHero
				agent="claude"
				title="Connect Claude to AgentReacher"
				description="Add the AgentReacher connector in claude.ai or the Claude app, sign in once, and Claude can start working inside your social media workspace. Connectors sync between claude.ai and the app."
				primaryCta={{ label: "Start Now", to: "/choose-plan" }}
				secondaryCta={{ label: "Sign in", to: "/login" }}
			/>

			<SetupSection
				id={CLAUDE_SECTION_ID}
				title="Connect Claude"
				description="Add AgentReacher as a custom connector in claude.ai or the Claude app."
				steps={STEPS}
				variant="claude"
				switchLink={{
					href: `#${CLAUDE_CODE_SECTION_ID}`,
					label: "Use Claude Code instead",
				}}
			/>

			<SetupSection
				id={CLAUDE_CODE_SECTION_ID}
				title="Connect Claude Code"
				description="Claude Code uses the same AgentReacher MCP server from your terminal."
				steps={CLAUDE_CODE_STEPS}
				variant="claude-code"
				switchLink={{
					href: `#${CLAUDE_SECTION_ID}`,
					label: "Use Claude.ai / Claude app instead",
				}}
			/>

			<SetupExamplePrompts agentName="Claude" />
		</MarketingPageShell>
	);
}
